import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Store, Gem, PawPrint, Loader2, Info, Sparkles, TrendingUp } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { themeOf, ARENA, PET_SLOT_THEME, rarityOf } from '../lib/classTheme';
import {
  ArenaFloor, ArenaTitle, ArenaTabs, ArenaEmpty, RarityBadge, Laurel,
} from '../components/ArenaFrame';
import { Sheet, SkeletonList } from '../components/ui';

export default function PetShop() {
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const { user, load } = useAuth();
  const [slot, setSlot] = useState(sp.get('slot') || 'head');
  const [preview, setPreview] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['petShop'], queryFn: () => api.get('/pets/shop').then(r => r.data),
  });
  const { data: pet } = useQuery({ queryKey: ['pet'], queryFn: () => api.get('/pets').then(r => r.data) });

  const buy = useMutation({
    mutationFn: ({ itemId, rarity }) => api.post('/pets/buy-item', { itemId, rarity }),
    onSuccess: ({ data: d }) => {
      toast.success(`✨ ${d.name} acquired! −💎${d.price}`);
      setPreview(null);
      qc.invalidateQueries(['petShop', 'pet', 'petInventory']);
      load();
    },
    onError: e => toast.error(e.message),
  });

  useEffect(() => { setSp(slot ? { slot } : {}, { replace: true }); }, [slot]);

  if (isLoading || !data) return <SkeletonList n={4} h="h-40" />;

  const t = themeOf(data.classKey);
  const items = data.catalog?.[slot] || [];
  const balance = data.balance ?? user?.points_balance ?? 0;

  if (!pet?.pet) {
    return (
      <ArenaEmpty emoji="🐾" title="No companion yet"
        sub="Adopt a companion first to browse their gear."
        action={<Link to="/pet" className="btn-primary btn-sm">Choose companion</Link>} />
    );
  }

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena arena-arches overflow-hidden">
        <ArenaFloor classKey={data.classKey} className="p-5" showCrowd={false}>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl grid place-items-center shrink-0"
              style={{
                background: `${t.hex}28`,
                border: `1.5px solid ${t.hex}`,
                boxShadow: `0 0 20px -4px ${t.hex}`,
              }}>
              <PawPrint size={22} style={{ color: ARENA.goldL }} />
            </div>
            <div className="flex-1 min-w-0 text-white">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Laurel size={18} color={ARENA.goldL} />
                <span className="text-[9px] font-display font-black uppercase tracking-[0.2em]"
                  style={{ color: ARENA.goldL }}>Ornamenta</span>
              </div>
              <h1 className="font-display text-xl font-extrabold tracking-wide"
                style={{ color: ARENA.goldL, textShadow: '0 2px 14px rgba(201,151,58,.45)' }}>
                Companion Gear
              </h1>
              <p className="text-[11px] font-semibold" style={{ color: 'rgba(232,220,200,.7)' }}>
                {pet.pet.name} · {pet.pet.species}
              </p>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between rounded-2xl px-4 py-2.5"
            style={{ background: 'rgba(232,200,116,.14)', border: '1px solid rgba(232,200,116,.28)' }}>
            <span className="text-[10px] font-display font-black uppercase tracking-wider"
              style={{ color: 'rgba(232,220,200,.7)' }}>Treasury</span>
            <span className="font-display text-xl font-extrabold flex items-center gap-1.5"
              style={{ color: ARENA.goldL }}>
              <Gem size={16} /> {compact(balance)}
            </span>
          </div>
        </ArenaFloor>

        <div className="p-3" style={{ background: 'var(--sand-100)' }}>
          <Link to="/pet" className="btn-ghost btn-sm w-full">
            <PawPrint size={14} /> Back to companion
          </Link>
        </div>
      </div>

      {/* ═══ EARN HINT ═══ */}
      {balance < 300 && (
        <div className="card p-4" style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
          <p className="font-display font-bold text-sm flex items-center gap-1.5"
            style={{ color: ARENA.goldD }}>
            <Info size={14} /> Need more points?
          </p>
          <ul className="text-xs font-semibold mt-2 space-y-1" style={{ color: 'var(--stone-600)' }}>
            <li>🛒 Shop at local merchants — <b>1 point per unit</b></li>
            <li>🤝 Invite friends — <b>7% of their earnings</b></li>
            <li>⚔️ Win duels — <b>60 points</b></li>
          </ul>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Link to="/shops" className="btn-primary btn-sm">Find shops</Link>
            <Link to="/arena" className="btn-ghost btn-sm">Fight</Link>
          </div>
        </div>
      )}

      {/* ═══ SLOT TABS ═══ */}
      <ArenaTabs value={slot} onChange={setSlot}
        tabs={Object.entries(PET_SLOT_THEME).map(([k, s]) => ({
          key: k, label: s.label, icon: s.icon,
        }))} />

      <div className="flex items-center gap-2 px-1">
        <span className="text-xl">{PET_SLOT_THEME[slot].icon}</span>
        <div>
          <p className="font-display font-bold text-sm">{PET_SLOT_THEME[slot].label}</p>
          <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
            {PET_SLOT_THEME[slot].hint}
          </p>
        </div>
      </div>

      {/* ═══ ITEMS ═══ */}
      {!items.length ? (
        <ArenaEmpty emoji="🐾" title="Not crafted yet"
          sub="This gear is still being forged. Check back soon!" />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((it, i) => (
            <motion.div key={`${it.itemId}-${it.rarity}`}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}>
              <ShopCard item={it} onClick={() => setPreview(it)} />
            </motion.div>
          ))}
        </div>
      )}

      {/* ═══ RARITY LEGEND ═══ */}
      <RarityLegend items={items} />

      {/* ═══ PREVIEW ═══ */}
      <Sheet open={!!preview} onClose={() => setPreview(null)} title={preview?.name || ''}>
        {preview && <BuyPreview item={preview} balance={balance}
          busy={buy.isPending} slotMeta={PET_SLOT_THEME[preview.slot]}
          onBuy={() => buy.mutate({ itemId: preview.itemId, rarity: preview.rarity })} />}
      </Sheet>
    </div>
  );
}

/* ============================================================
   SHARED SHOP COMPONENTS
   ============================================================ */
export function ShopCard({ item, onClick }) {
  const r = rarityOf(item.rarity);
  return (
    <motion.button onClick={onClick} whileTap={{ scale: .95 }} whileHover={{ y: -3 }}
      className="relative w-full rounded-2xl overflow-hidden text-left"
      style={{
        padding: 2,
        background: item.owned
          ? 'linear-gradient(135deg,#34d399,#059669)'
          : `linear-gradient(135deg,${r.hex},${r.glow})`,
        boxShadow: r.tier >= 4 ? r.shadowCss : '0 3px 14px -5px rgba(61,51,37,.3)',
        opacity: item.available ? 1 : .5,
      }}>
      <div className="rounded-[14px] overflow-hidden" style={{ background: 'var(--marble)' }}>
        <div className="relative aspect-square"
          style={{ background: `radial-gradient(circle at 50% 35%, ${r.hex}18, var(--stone-100))` }}>
          {item.image
            ? <img src={item.thumb || item.image} alt="" className="w-full h-full object-cover" loading="lazy" />
            : <div className="w-full h-full grid place-items-center text-3xl opacity-30">{item.icon}</div>}

          {r.tier === 5 && (
            <motion.div className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(115deg,transparent 35%,rgba(255,255,255,.45) 50%,transparent 65%)' }}
              animate={{ x: ['-120%', '120%'] }}
              transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2.5 }} />
          )}

          {item.owned && (
            <span className="absolute top-1.5 left-1.5 chip-mint !text-[7px]">OWNED</span>
          )}
          {!item.available && (
            <div className="absolute inset-0 grid place-items-center"
              style={{ background: 'rgba(36,30,22,.55)' }}>
              <span className="text-[9px] font-display font-black text-white px-2 py-1 rounded"
                style={{ background: 'rgba(0,0,0,.5)' }}>SOON</span>
            </div>
          )}
        </div>

        <div className="p-2">
          <RarityBadge rarity={item.rarity} />
          <p className="font-bold text-[11px] leading-tight line-clamp-2 min-h-[26px] mt-0.5">
            {item.name}
          </p>
          <div className="flex items-center gap-1 mt-1">
            <TrendingUp size={9} style={{ color: 'var(--stone-400)' }} />
            <span className="text-[9px] font-black" style={{ color: r.hex }}>+{item.totalStats}</span>
          </div>
          <div className={`mt-1.5 py-1 rounded-lg text-[10px] font-display font-black text-center`}
            style={item.owned
              ? { background: 'rgba(52,211,153,.16)', color: '#047857' }
              : item.affordable
                ? { background: 'rgba(201,151,58,.16)', color: ARENA.goldD }
                : { background: 'var(--stone-100)', color: 'var(--stone-400)' }}>
            {item.owned ? '✓ OWNED' : `💎 ${compact(item.price)}`}
          </div>
        </div>
      </div>
    </motion.button>
  );
}

export function BuyPreview({ item, balance, onBuy, busy, slotMeta }) {
  const r = rarityOf(item.rarity);
  const after = balance - item.price;

  return (
    <div className="space-y-4">
      <div className="relative rounded-2xl overflow-hidden"
        style={{ padding: 3, background: `linear-gradient(135deg,${r.hex},${r.glow})`, boxShadow: r.shadowCss }}>
        <div className="rounded-xl overflow-hidden relative"
          style={{ background: `radial-gradient(circle at 50% 40%, ${r.hex}20, var(--stone-100))` }}>
          {item.image
            ? <img src={item.image} alt="" className="w-full aspect-square object-cover" />
            : <div className="w-full aspect-square grid place-items-center text-5xl opacity-30">{item.icon}</div>}
          {r.tier >= 4 && (
            <motion.div className="absolute inset-0 pointer-events-none"
              style={{ background: `linear-gradient(115deg,transparent 40%,${r.hex}44 50%,transparent 60%)` }}
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.5 }} />
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-lg">{slotMeta?.icon || item.icon}</span>
          <RarityBadge rarity={item.rarity} size="lg" showLatin />
          <span className="chip-ink">{slotMeta?.label || item.slot}</span>
        </div>
        <h3 className="font-display text-xl font-extrabold">{item.name}</h3>
        {item.description && (
          <p className="text-sm mt-1" style={{ color: 'var(--stone-500)' }}>{item.description}</p>
        )}
      </div>

      {item.stats && Object.keys(item.stats).length > 0 && (
        <div className="card p-3.5">
          <div className="flex items-center justify-between mb-2.5">
            <p className="label !mb-0">Bonuses</p>
            <span className="chip-gold"><TrendingUp size={9} /> +{item.totalStats}</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(item.stats).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg"
                style={{ background: 'var(--sand-100)' }}>
                <span className="text-[11px] font-semibold capitalize" style={{ color: 'var(--stone-600)' }}>
                  {k.replace('_', '')}
                </span>
                <span className="text-sm font-display font-black" style={{ color: r.hex }}>+{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card p-3.5 space-y-2">
        <Row l="Price" v={`💎 ${compact(item.price)}`} bold />
        <Row l="Treasury" v={`💎 ${compact(balance)}`} />
        <div className="divider !my-1" />
        <Row l="After purchase" v={`💎 ${compact(Math.max(0, after))}`}
          color={after < 0 ? ARENA.blood : '#047857'} bold />
      </div>

      {item.owned ? (
        <div className="p-3 rounded-xl text-center" style={{ background: 'rgba(52,211,153,.12)' }}>
          <p className="font-display font-bold text-sm text-mint-600">✓ Already owned</p>
        </div>
      ) : !item.available ? (
        <div className="p-3 rounded-xl text-center" style={{ background: 'var(--stone-100)' }}>
          <p className="font-display font-bold text-sm" style={{ color: 'var(--stone-500)' }}>
            Not available yet
          </p>
        </div>
      ) : after < 0 ? (
        <div className="space-y-2">
          <div className="p-3 rounded-xl text-center"
            style={{ background: 'rgba(139,38,53,.08)', border: `1px solid ${ARENA.blood}44` }}>
            <p className="font-display font-bold text-sm" style={{ color: ARENA.blood }}>
              Need {compact(item.price - balance)} more points
            </p>
          </div>
          <Link to="/shops" className="btn-primary w-full">Shop local to earn</Link>
        </div>
      ) : (
        <button onClick={onBuy} disabled={busy}
          className="btn-primary w-full py-3 font-display font-black"
          style={{ boxShadow: r.shadowCss }}>
          {busy ? <><Loader2 size={16} className="animate-spin" /> Acquiring…</>
            : <><Sparkles size={16} /> Buy for 💎{compact(item.price)}</>}
        </button>
      )}
    </div>
  );
}

export function RarityLegend({ items = [] }) {
  return (
    <div className="card p-3.5">
      <ArenaTitle title="Rarity tiers" latin="Gradus" />
      <div className="space-y-1.5">
        {['common', 'uncommon', 'rare', 'epic', 'legendary'].map(k => {
          const r = rarityOf(k);
          const sample = items.find(x => x.rarity === k);
          return (
            <div key={k} className="flex items-center gap-2.5">
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.hex }} />
              <span className="text-xs font-display font-black w-20" style={{ color: r.hex }}>
                {r.label}
              </span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--stone-200)' }}>
                <div className="h-full rounded-full" style={{ width: `${r.tier * 20}%`, background: r.hex }} />
              </div>
              {sample && (
                <span className="text-[10px] font-bold w-20 text-right" style={{ color: 'var(--stone-400)' }}>
                  💎{compact(sample.price)} · +{sample.totalStats}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] font-semibold mt-2.5" style={{ color: 'var(--stone-400)' }}>
        Higher rarity = far stronger stats and unique visual design. Sell back for 40%.
      </p>
    </div>
  );
}

function Row({ l, v, bold, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${bold ? 'font-display font-bold' : 'font-semibold'}`}
        style={{ color: bold ? 'var(--stone-800)' : 'var(--stone-500)' }}>{l}</span>
      <span className={bold ? 'font-display font-black' : 'font-bold'} style={{ color }}>{v}</span>
    </div>
  );
}